import { ChangeEvent, ComponentProps, ForwardedRef, ReactNode, forwardRef, useState } from "react"
import { Spacer } from "../../../../../components/spacer/Spacer"
import { CheckboxChecked, Checkbox as CheckboxIcon } from "../../../../../images/xd/icons"
import { ColorType, CSSProp, styled, theme } from "../../../../../styles/stitches.config"
import { Div } from "../../../../../components/div/Div"
import { CheckboxLabel, SCheckboxRow, SCheckboxWrapper, SCheckboxedSectionWrapper } from "./Checkbox.styled"
import { FormError } from "./Form"

const HiddenCheckbox = styled("input", {
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  width: 1,
  overflow: "hidden",
  position: "absolute",
  whiteSpace: "nowrap",
})

type CheckboxProps = {
  label: ReactNode
  bordered?: boolean
  align?: "start" | "center" | "end"
  value?: ComponentProps<"input">["checked"]
  onChange?: ComponentProps<"input">["onChange"]
  onBlur?: ComponentProps<"input">["onBlur"]
  error?: string
  // TODO pouziva se asi jen pro padding? zkontrolovat, kde to vsude je a pripadne vyhodit
  errorMsgCSS?: CSSProp
  checkboxCheckedColor?: ColorType
}

function CheckboxRaw(props: CheckboxProps, ref: ForwardedRef<HTMLInputElement>) {
  const {
    value: checked,
    error,
    label,
    bordered,
    checkboxCheckedColor = "secondary",
    errorMsgCSS,
    align = "center",
    ...field
  } = props

  return (
    <Div>
      <SCheckboxRow bordered={bordered} align={align}>
        <HiddenCheckbox ref={ref} type="checkbox" checked={checked} onBlur={field.onBlur} onChange={field.onChange} />
        <SCheckboxWrapper>
          {checked ? (
            <CheckboxChecked color={theme.colors[checkboxCheckedColor].value} />
          ) : (
            <CheckboxIcon id="checkbox-unchecked" color={theme.colors.textsAlt.value} />
          )}
        </SCheckboxWrapper>
        <Spacer size={theme.space.s6} />
        {label && <CheckboxLabel>{label}</CheckboxLabel>}
      </SCheckboxRow>
      {error && (
        <FormError
          align="start"
          css={{
            paddingLeft: theme.space.s8,
            ...errorMsgCSS,
          }}
        >
          {error}
        </FormError>
      )}
    </Div>
  )
}

export const Checkbox = forwardRef(CheckboxRaw)

type CheckboxedSectionProps = {
  checkboxLabel: ReactNode
  hiddenContent: ReactNode
  open?: boolean
  onCollapse?: () => void
  onOpen?: () => void
}

export function CheckboxedSection(props: CheckboxedSectionProps) {
  const { open, checkboxLabel, hiddenContent, onCollapse, onOpen } = props
  const [checked, setChecked] = useState(open ?? false)

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.currentTarget.checked
    setChecked(value)
    const externalHandler = value ? onOpen : onCollapse
    externalHandler?.()
  }

  return (
    <SCheckboxedSectionWrapper open={checked}>
      <Checkbox value={checked} label={checkboxLabel} onChange={handleChange} />
      {checked && (
        <>
          <Spacer size={theme.space.s6} />
          {hiddenContent}
        </>
      )}
    </SCheckboxedSectionWrapper>
  )
}
